import type { ContextAttributesName, ContextAttributesResponse } from '../types/types';

import { AttributeReference } from './AttributeReference';

/**
 * Process context attributes from an API response.
 * Normalizes attribute names using AttributeReference.
 *
 * @param contextAttributes - The API response containing context attributes.
 */
export function processContextAttributes(contextAttributes: ContextAttributesResponse): void {
  contextAttributes.items.forEach((contextAttributeItem) => {
    // The API can omit `names` on an item; iterating undefined throws "not iterable".
    // oxlint-disable-next-line no-param-reassign
    contextAttributeItem.names = processContextAttributesNames(contextAttributeItem.names ?? []);
  });
}

/**
 * Process an array of context attribute names.
 * Normalizes each name using AttributeReference, and drops entries whose
 * reference does not resolve to a non-empty string (e.g. raw "/" or "" inputs,
 * which would otherwise leave `name` as `undefined` and crash any consumer
 * that re-runs AttributeReference on the result).
 *
 * @param names - The array of attribute names to process.
 * @returns The processed array of attribute names.
 */
export function processContextAttributesNames(names: ContextAttributesName[]): ContextAttributesName[] {
  const processed: ContextAttributesName[] = [];
  for (const name of names) {
    const normalized = new AttributeReference(name.name).toString();
    if (normalized) {
      processed.push({ ...name, name: normalized });
    }
  }
  return processed;
}
